import { useContext } from 'react'

import classNames from 'classnames'
import { Field, useFormikContext } from 'formik'
import { AppQuizUsageQuestionMissingWord } from 'globalTypes'
import { get } from 'lodash'
import reactStringReplace from 'react-string-replace'

import CoverImage from 'Root/App/CoverImage'
import Input from 'Root/App/Form/Input'
import { Context as StateContext } from 'Root/App/Quiz/StateContext'

import FailureFeedback from '../../Feedback/Failure'
import SuccessFeedback from '../../Feedback/Success'
import { getMatchesFromQuestion } from './get-matches-from-question'
import styles from './index.module.css'

interface Props {
  question: AppQuizUsageQuestionMissingWord
}

const MissingWord = ({ question }: Props) => {
  const {
    state: { answers, content },
  } = useContext(StateContext)
  const { values, setFieldValue, setFieldTouched } = useFormikContext()
  const value = get(values, ['answers', question.id], [])

  const questionMatches = getMatchesFromQuestion(question.title)

  const hasAnswered = Boolean(get(answers, question.id))
  const hasAnsweredCorrectly =
    hasAnswered &&
    questionMatches.every((match, index) =>
      match.includes(answers[question.id][index])
    )

  const displayValidation = content.showAnswer && hasAnswered

  const displaySuccessFeedback =
    hasAnsweredCorrectly && Boolean(question.successFeedback)
  const displayFailureFeedback =
    hasAnswered && !hasAnsweredCorrectly && Boolean(question.failureFeedback)

  // used to count input fields
  let fields = 0

  return (
    <div className="flex flex-col gap-3">
      {displaySuccessFeedback && (
        <SuccessFeedback>{question.successFeedback}</SuccessFeedback>
      )}
      {displayFailureFeedback && (
        <FailureFeedback>{question.failureFeedback}</FailureFeedback>
      )}

      {Boolean(question.image) && <CoverImage imageUrl={question.image} />}

      <div>
        {reactStringReplace(
          question.title,
          /(\{=[^\}]+\})/g,
          (_match, index) => {
            const isValid = questionMatches[fields].includes(value?.[fields])
            const id = `answers.${question.id}.${fields++}`

            return (
              <Field
                as={Input}
                className={classNames(styles.field, {
                  [styles.success]: displayValidation && isValid,
                  [styles.failure]: displayValidation && !isValid,
                })}
                id={id}
                name={id}
                onChange={({ target }) => {
                  setFieldValue(id, target.value)
                  setFieldTouched(id, true)
                }}
                key={index}
              />
            )
          }
        )}
      </div>
    </div>
  )
}

export default MissingWord
